import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
  import { 
  Plus, 
  Search, 
  Truck,
  Edit2,
  Trash2,
  X,
  Clock,
  AlertCircle,
  Wrench,
  AlertTriangle,
  CheckCircle,
  History,
  Power,
  Timer
} from 'lucide-react';
import MaintenanceAlert from '../components/MaintenanceAlert';
import { authHeaders } from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NON_OP_CATEGORIES = ['Embarque', 'Desembarque', 'Manobra Oficina', 'Translado', 'Operação Interna'];

const nowLocalDatetime = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const machineTypes = [
  'Trator de Esteiras',
  'Escavadeira Hidráulica',
  'Trator de Pneus',
  'Pá Carregadeira',
  'Motoniveladora',
  'Retroescavadeira',
  'Rolo Compactador',
  'Empilhadeira'
];

const maintenanceIntervals = [250, 300, 500, 600, 750, 1000];

const maintenanceStatusConfig = {
  ok: { bgClass: 'bg-green-500/10 border-green-500/30', barClass: 'bg-green-500', textColor: 'text-green-400', icon: CheckCircle, label: 'OK' },
  attention: { bgClass: 'bg-yellow-500/10 border-yellow-500/30', barClass: 'bg-yellow-500', textColor: 'text-yellow-400', icon: AlertTriangle, label: 'Atenção' },
  urgent: { bgClass: 'bg-red-500/10 border-red-500/30', barClass: 'bg-red-500', textColor: 'text-red-400', icon: AlertCircle, label: 'Urgente' }
};

export default function Machines({ userRole = 'admin' }) {
  const canEdit = ['admin', 'gestor'].includes(userRole);
  const canRegisterNonOp = ['admin', 'gestor', 'supervisor'].includes(userRole);
  
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [editingMachine, setEditingMachine] = useState(null);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);
  const [form, setForm] = useState({
    type: '',
    brand: '',
    model: '',
    serial_number: '',
    initial_hour_meter: 0,
    current_hour_meter: 0,
    hours_since_maintenance: 0,
    maintenance_interval: 300,
    notes: ''
  });
  const [maintenanceForm, setMaintenanceForm] = useState({
    hour_meter: 0,
    maintenance_type: 'Revisão Programada',
    observations: ''
  });

  const [showNonOpModal, setShowNonOpModal] = useState(false);
  const [nonOpList, setNonOpList] = useState([]);
  const [nonOpLoading, setNonOpLoading] = useState(false);
  const [nonOpSaving, setNonOpSaving] = useState(false);
  const [nonOpForm, setNonOpForm] = useState({
    categoria: 'Embarque',
    horimetro_inicial: 0,
    horimetro_final: 0,
    data_hora: '',
    observacoes: ''
  });

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await fetch(`${API_URL}/api/machines`, {
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setMachines(data);
      } else {
        toast.error('Erro ao carregar máquinas');
      }
    } catch (error) {
      console.error('Erro:', error);
      toast.error('Erro de conexão');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingMachine 
      ? `${API_URL}/api/machines/${editingMachine.id}`
      : `${API_URL}/api/machines`;
    
    try {
      const response = await fetch(url, {
        method: editingMachine ? 'PUT' : 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(editingMachine ? 'Máquina atualizada!' : 'Máquina cadastrada!');
        setShowModal(false);
        resetForm();
        fetchMachines();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao salvar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta máquina?')) return;

    try {
      const response = await fetch(`${API_URL}/api/machines/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });

      if (response.ok) {
        toast.success('Máquina excluída!');
        fetchMachines();
      } else {
        toast.error('Erro ao excluir');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const handleToggleStatus = async (machine) => {
    const action = machine.status === 'active' ? 'inativar' : 'ativar';
    if (!window.confirm(`Deseja ${action} a máquina ${machine.brand} ${machine.model} (${machine.serial_number})?`)) return;

    try {
      const response = await fetch(`${API_URL}/api/machines/${machine.id}/toggle-status`, {
        method: 'PATCH',
        headers: authHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchMachines();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao alterar status');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const openEditModal = (machine) => {
    setEditingMachine(machine);
    setForm({
      type: machine.type,
      brand: machine.brand,
      model: machine.model,
      serial_number: machine.serial_number,
      initial_hour_meter: machine.initial_hour_meter,
      current_hour_meter: machine.current_hour_meter || 0,
      hours_since_maintenance: machine.hours_since_maintenance || 0,
      maintenance_interval: machine.maintenance_interval || 300,
      notes: machine.notes || ''
    });
    setShowModal(true);
  };

  const openMaintenanceModal = (machine) => {
    setSelectedMachine(machine);
    setMaintenanceForm({
      hour_meter: machine.current_hour_meter,
      maintenance_type: 'Revisão Programada',
      observations: ''
    });
    setShowMaintenanceModal(true);
  };

  const openHistoryModal = async (machine) => {
    setSelectedMachine(machine);
    setShowHistoryModal(true);
    
    try {
      const response = await fetch(`${API_URL}/api/maintenance/history/${machine.id}`, {
        headers: authHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setMaintenanceHistory(data);
      }
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleMaintenanceSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await fetch(`${API_URL}/api/maintenance/register`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          machine_id: selectedMachine.id,
          ...maintenanceForm
        })
      });

      if (response.ok) {
        toast.success('Manutenção registrada! Contador zerado.');
        setShowMaintenanceModal(false);
        fetchMachines();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao registrar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    }
  };

  const openNonOpModal = async (machine) => {
    setSelectedMachine(machine);
    setNonOpForm({
      categoria: 'Embarque',
      horimetro_inicial: machine.current_hour_meter || 0,
      horimetro_final: machine.current_hour_meter || 0,
      data_hora: nowLocalDatetime(),
      observacoes: ''
    });
    setNonOpList([]);
    setShowNonOpModal(true);
    setNonOpLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/machines/${machine.id}/horas-nao-operacionais`, {
        headers: authHeaders()
      });
      if (response.ok) {
        setNonOpList(await response.json());
      }
    } catch (error) {
      toast.error('Erro ao carregar lançamentos');
    } finally {
      setNonOpLoading(false);
    }
  };

  const handleNonOpSubmit = async (e) => {
    e.preventDefault();
    if (parseFloat(nonOpForm.horimetro_final) < parseFloat(nonOpForm.horimetro_inicial)) {
      toast.error('O horímetro final não pode ser menor que o inicial.');
      return;
    }
    setNonOpSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/machines/${selectedMachine.id}/horas-nao-operacionais`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          categoria: nonOpForm.categoria,
          horimetro_inicial: parseFloat(nonOpForm.horimetro_inicial) || 0,
          horimetro_final: parseFloat(nonOpForm.horimetro_final) || 0,
          data_hora: nonOpForm.data_hora ? new Date(nonOpForm.data_hora).toISOString() : undefined,
          observacoes: nonOpForm.observacoes || undefined
        })
      });
      if (response.ok) {
        const created = await response.json();
        toast.success('Horas não operacionais registradas! Horímetro atualizado.');
        setNonOpList([created, ...nonOpList]);
        setNonOpForm({
          categoria: 'Embarque',
          horimetro_inicial: created.horimetro_final,
          horimetro_final: created.horimetro_final,
          data_hora: nowLocalDatetime(),
          observacoes: ''
        });
        fetchMachines();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Erro ao registrar');
      }
    } catch (error) {
      toast.error('Erro de conexão');
    } finally {
      setNonOpSaving(false);
    }
  };

  const resetForm = () => {
    setEditingMachine(null);
    setForm({
      type: '',
      brand: '',
      model: '',
      serial_number: '',
      initial_hour_meter: 0,
      maintenance_interval: 300,
      notes: ''
    });
  };

  const filteredMachines = machines.filter(m => 
    m.brand.toLowerCase().includes(search.toLowerCase()) ||
    m.model.toLowerCase().includes(search.toLowerCase()) ||
    m.serial_number.toLowerCase().includes(search.toLowerCase()) ||
    m.type.toLowerCase().includes(search.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6" data-testid="machines-page">
      {/* Alerta de Manutenção */}
      <MaintenanceAlert showCompact={true} />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">Máquinas e Veículos</h1>
          <p className="text-gray-400 mt-1">
            {canEdit ? 'Gerencie suas máquinas e veículos da frota' : 'Visualize as máquinas e veículos'}
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            data-testid="add-machine-button"
            className="flex items-center justify-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
          >
            <Plus size={20} />
            <span>Nova Máquina</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Buscar por marca, modelo, série ou tipo..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-12 pr-4 py-3 bg-[#111] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
          data-testid="search-input"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-10 h-10 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredMachines.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredMachines.map((machine) => {
            const statusConfig = maintenanceStatusConfig[machine.maintenance_status] || maintenanceStatusConfig.ok;
            const StatusIcon = statusConfig.icon;
            
            return (
              <div 
                key={machine.id} 
                className={`bg-[#111] border border-gray-800 rounded-xl p-4 lg:p-6 hover:border-gray-700 transition-all ${machine.status !== 'active' ? 'opacity-60' : ''}`}
                data-testid={`machine-card-${machine.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-500/10 p-2 rounded-lg">
                      <Truck size={20} className="text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">{machine.brand} {machine.model}</h3>
                      <p className="text-gray-500 text-sm">{machine.type}</p>
                    </div>
                  </div>
                  {canEdit ? (
                    <button
                      onClick={() => handleToggleStatus(machine)}
                      data-testid={`toggle-status-machine-${machine.id}`}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded cursor-pointer transition-colors ${
                        machine.status === 'active' 
                          ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
                          : 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      }`}
                      title={machine.status === 'active' ? 'Clique para inativar' : 'Clique para ativar'}
                    >
                      <Power size={12} />
                      {machine.status === 'active' ? 'Ativo' : 'Inativo'}
                    </button>
                  ) : (
                    <span className={`text-xs px-2 py-1 rounded ${
                      machine.status === 'active' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-red-500/20 text-red-400'
                    }`}>
                      {machine.status === 'active' ? 'Ativo' : 'Inativo'}
                    </span>
                  )}
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Série</span>
                    <span className="text-white font-mono">{machine.serial_number}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Horímetro Atual</span>
                    <span className="text-white flex items-center gap-1">
                      <Clock size={14} className="text-yellow-500" />
                      {machine.current_hour_meter?.toFixed(1) || '0'}h
                    </span>
                  </div>
                </div>

                {/* Dados de veículo (somente leitura, sincronizado do Inventário) — Etapa 5 */}
                {machine.is_vehicle && (
                  <div className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-1.5"
                    data-testid={`machine-vehicle-info-${machine.id}`}>
                    <div className="flex items-center gap-1.5 mb-1">
                      <Truck size={13} className="text-blue-400" />
                      <span className="text-blue-300 text-xs font-semibold uppercase tracking-wide">Veículo</span>
                    </div>
                    {machine.placa && (
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Placa</span><span className="text-white font-mono">{machine.placa}</span></div>
                    )}
                    {machine.chassis && (
                      <div className="flex justify-between text-xs"><span className="text-gray-500">Chassis</span><span className="text-white font-mono">{machine.chassis}</span></div>
                    )}
                    {machine.renavam && (
                      <div className="flex justify-between text-xs"><span className="text-gray-500">RENAVAM</span><span className="text-white font-mono">{machine.renavam}</span></div>
                    )}
                    {machine.cnh_categoria_exigida && (
                      <div className="flex justify-between text-xs"><span className="text-gray-500">CNH exigida</span><span className="text-white">Cat. {machine.cnh_categoria_exigida}</span></div>
                    )}
                  </div>
                )}

                {/* Status de Manutenção */}
                <div className={`p-3 rounded-lg mb-4 border ${statusConfig.bgClass}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <StatusIcon size={18} className={statusConfig.textColor} />
                      <span className={`text-sm font-medium ${statusConfig.textColor}`}>
                        {statusConfig.label}
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-semibold">
                        {machine.hours_until_maintenance?.toFixed(0)}h
                      </p>
                      <p className="text-gray-500 text-xs">p/ revisão</p>
                    </div>
                  </div>
                  <div className="mt-2">
                    <div className="flex justify-between text-xs text-gray-400 mb-1">
                      <span>Desde última revisão</span>
                      <span>{machine.hours_since_maintenance?.toFixed(1)}h / {machine.maintenance_interval}h</span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${statusConfig.barClass}`}
                        style={{ 
                          width: `${Math.min(100, (machine.hours_since_maintenance / machine.maintenance_interval) * 100)}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Botões de ação */}
                <div className="flex gap-2 pt-4 border-t border-gray-800">
                  {canEdit && (
                    <>
                      <button
                        onClick={() => openMaintenanceModal(machine)}
                        className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-500/10 hover:bg-green-500/20 text-green-400 rounded-lg transition-colors text-sm"
                        data-testid={`maintenance-machine-${machine.id}`}
                        title="Registrar Revisão"
                      >
                        <Wrench size={16} />
                        Revisão
                      </button>
                      <button
                        onClick={() => openEditModal(machine)}
                        className="flex items-center justify-center px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors"
                        data-testid={`edit-machine-${machine.id}`}
                        title="Editar"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(machine.id)}
                        className="flex items-center justify-center px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-lg transition-colors"
                        data-testid={`delete-machine-${machine.id}`}
                        title="Excluir"
                      >
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                  {canRegisterNonOp && (
                    <button
                      onClick={() => openNonOpModal(machine)}
                      className="flex items-center justify-center px-3 py-2 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-lg transition-colors"
                      data-testid={`non-op-machine-${machine.id}`}
                      title="Horas Não Operacionais"
                    >
                      <Timer size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => openHistoryModal(machine)}
                    className="flex items-center justify-center px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-lg transition-colors"
                    title="Histórico"
                  >
                    <History size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-[#111] border border-gray-800 rounded-xl p-12 text-center">
          <AlertCircle size={48} className="text-gray-600 mx-auto mb-4" />
          <h3 className="text-white text-lg font-semibold mb-2">Nenhuma máquina encontrada</h3>
          <p className="text-gray-500 mb-6">
            {search ? 'Tente ajustar sua busca' : (canEdit ? 'Comece cadastrando sua primeira máquina' : 'Nenhuma máquina cadastrada ainda')}
          </p>
          {!search && canEdit && (
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
            >
              <Plus size={20} />
              Cadastrar Máquina
            </button>
          )}
        </div>
      )}

      {/* Modal - Cadastro/Edição de Máquina */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {editingMachine ? 'Editar Máquina' : 'Nova Máquina'}
              </h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Tipo *</label>
                <select
                  value={form.type}
                  onChange={(e) => setForm({...form, type: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                  required
                  data-testid="input-type"
                >
                  <option value="">Selecione o tipo</option>
                  {machineTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Marca *</label>
                  <input
                    type="text"
                    value={form.brand}
                    onChange={(e) => setForm({...form, brand: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                    placeholder="Ex: Caterpillar"
                    required
                    data-testid="input-brand"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Modelo *</label>
                  <input
                    type="text"
                    value={form.model}
                    onChange={(e) => setForm({...form, model: e.target.value})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                    placeholder="Ex: 320D"
                    required
                    data-testid="input-model"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Número de Série *</label>
                <input
                  type="text"
                  value={form.serial_number}
                  onChange={(e) => setForm({...form, serial_number: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors font-mono"
                  placeholder="Ex: CAT0320D12345"
                  required
                  data-testid="input-serial"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Horímetro Inicial</label>
                  <input
                    type="number"
                    value={form.initial_hour_meter}
                    onChange={(e) => setForm({...form, initial_hour_meter: parseFloat(e.target.value) || 0})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                    placeholder="0"
                    min="0"
                    step="0.1"
                    data-testid="input-hour-meter"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Intervalo Revisão *</label>
                  <select
                    value={form.maintenance_interval}
                    onChange={(e) => setForm({...form, maintenance_interval: parseInt(e.target.value)})}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                    required
                    data-testid="input-maintenance-interval"
                  >
                    {maintenanceIntervals.map(interval => (
                      <option key={interval} value={interval}>{interval}h</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Veículo: dados sincronizados do Inventário (somente leitura) — Etapa 5 */}
              {editingMachine?.is_vehicle && (
                <div className="rounded-lg border border-blue-500/30 bg-blue-500/5 p-3 space-y-3" data-testid="machine-vehicle-fields">
                  <div className="flex items-center gap-2">
                    <Truck size={15} className="text-blue-400" />
                    <span className="text-blue-300 text-sm font-semibold">Dados do Veículo</span>
                    <span className="text-[10px] text-gray-500 ml-auto">somente leitura · sincronizado do Inventário</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Placa</label>
                      <input type="text" value={editingMachine.placa || '—'} readOnly disabled
                        data-testid="vehicle-placa"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-300 font-mono text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">RENAVAM</label>
                      <input type="text" value={editingMachine.renavam || '—'} readOnly disabled
                        data-testid="vehicle-renavam"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-300 font-mono text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Chassis</label>
                      <input type="text" value={editingMachine.chassis || '—'} readOnly disabled
                        data-testid="vehicle-chassis"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-300 font-mono text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">CNH exigida</label>
                      <input type="text" value={editingMachine.cnh_categoria_exigida ? `Cat. ${editingMachine.cnh_categoria_exigida}` : '—'} readOnly disabled
                        data-testid="vehicle-cnh"
                        className="w-full px-3 py-2 bg-[#0A0A0A] border border-gray-800 rounded-lg text-gray-300 text-sm" />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-500">Para alterar estes dados, edite o ativo no módulo Inventário.</p>
                </div>
              )}

              {/* Campos adicionais para edição (Admin) */}
              {editingMachine && canEdit && (
                <div className="border border-yellow-500/30 rounded-lg p-4 space-y-4">
                  <p className="text-sm text-yellow-400 font-medium">Correção de Dados (Admin)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Horímetro Atual</label>
                      <input
                        type="number"
                        value={form.current_hour_meter}
                        onChange={(e) => setForm({...form, current_hour_meter: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                        min="0"
                        step="0.1"
                        data-testid="input-current-hour-meter"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Horas desde Revisão</label>
                      <input
                        type="number"
                        value={form.hours_since_maintenance}
                        onChange={(e) => setForm({...form, hours_since_maintenance: parseFloat(e.target.value) || 0})}
                        className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors"
                        min="0"
                        step="0.1"
                        data-testid="input-hours-since-maintenance"
                      />
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm text-gray-400 mb-2">Observações</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({...form, notes: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Informações adicionais..."
                  rows={3}
                  data-testid="input-notes"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-lg transition-colors"
                  data-testid="submit-machine"
                >
                  {editingMachine ? 'Salvar' : 'Cadastrar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal - Registrar Manutenção */}
      {showMaintenanceModal && selectedMachine && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-white">Registrar Revisão</h2>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedMachine.brand} {selectedMachine.model}
                </p>
              </div>
              <button 
                onClick={() => setShowMaintenanceModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleMaintenanceSubmit} className="p-4 lg:p-6 space-y-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-yellow-400 text-sm">
                  Ao registrar a revisão, o contador de horas será zerado e a contagem reiniciará.
                </p>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Horímetro Atual *</label>
                <input
                  type="number"
                  value={maintenanceForm.hour_meter}
                  onChange={(e) => setMaintenanceForm({...maintenanceForm, hour_meter: parseFloat(e.target.value) || 0})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                  required
                  min="0"
                  step="0.1"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Tipo de Revisão *</label>
                <select
                  value={maintenanceForm.maintenance_type}
                  onChange={(e) => setMaintenanceForm({...maintenanceForm, maintenance_type: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:outline-none transition-colors"
                  required
                >
                  <option value="Revisão Programada">Revisão Programada</option>
                  <option value="Troca de Óleo">Troca de Óleo</option>
                  <option value="Troca de Filtros">Troca de Filtros</option>
                  <option value="Revisão Geral">Revisão Geral</option>
                  <option value="Manutenção Corretiva">Manutenção Corretiva</option>
                  <option value="Outros">Outros</option>
                </select>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Observações</label>
                <textarea
                  value={maintenanceForm.observations}
                  onChange={(e) => setMaintenanceForm({...maintenanceForm, observations: e.target.value})}
                  className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-yellow-500 focus:outline-none transition-colors resize-none"
                  placeholder="Detalhes da revisão realizada..."
                  rows={3}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowMaintenanceModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-400 text-black font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Wrench size={18} />
                  Registrar Revisão
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal - Histórico de Manutenção */}
      {showHistoryModal && selectedMachine && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-white">Histórico de Revisões</h2>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedMachine.brand} {selectedMachine.model} - {selectedMachine.serial_number}
                </p>
              </div>
              <button 
                onClick={() => setShowHistoryModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              {maintenanceHistory.length > 0 ? (
                <div className="space-y-4">
                  {maintenanceHistory.map((record) => (
                    <div key={record.id} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-white font-medium">{record.maintenance_type}</p>
                          <p className="text-gray-500 text-sm">{formatDate(record.created_at)}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-500 font-mono">{record.hour_meter?.toFixed(1)}h</p>
                          <p className="text-gray-500 text-xs">Horímetro</p>
                        </div>
                      </div>
                      {record.observations && (
                        <p className="text-gray-400 text-sm mt-2 pt-2 border-t border-gray-800">
                          {record.observations}
                        </p>
                      )}
                      {record.performed_by && (
                        <p className="text-gray-500 text-xs mt-2">
                          Registrado por: {record.performed_by}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <History size={40} className="text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">Nenhuma revisão registrada</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Modal - Horas Não Operacionais */}
      {showNonOpModal && selectedMachine && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-[#111] border border-gray-800 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="non-op-modal">
            <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Timer size={20} className="text-purple-400" />
                  Horas Não Operacionais
                </h2>
                <p className="text-gray-400 text-sm mt-1">
                  {selectedMachine.brand} {selectedMachine.model} - {selectedMachine.serial_number}
                </p>
              </div>
              <button
                onClick={() => setShowNonOpModal(false)}
                className="p-2 text-gray-400 hover:text-white transition-colors"
                data-testid="non-op-close-btn"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-6">
              <form onSubmit={handleNonOpSubmit} className="space-y-4">
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3">
                  <p className="text-purple-300 text-sm">
                    Horímetro atual da máquina: <span className="font-mono font-semibold">{selectedMachine.current_hour_meter?.toFixed(1) || '0'}h</span>. Ao salvar, o horímetro atual será atualizado para o horímetro final informado.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Categoria *</label>
                    <select
                      value={nonOpForm.categoria}
                      onChange={(e) => setNonOpForm({ ...nonOpForm, categoria: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-colors"
                      required
                      data-testid="non-op-categoria"
                    >
                      {NON_OP_CATEGORIES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Data e Hora *</label>
                    <input
                      type="datetime-local"
                      value={nonOpForm.data_hora}
                      onChange={(e) => setNonOpForm({ ...nonOpForm, data_hora: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-colors"
                      required
                      data-testid="non-op-data-hora"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Horímetro Inicial *</label>
                    <input
                      type="number"
                      value={nonOpForm.horimetro_inicial}
                      onChange={(e) => setNonOpForm({ ...nonOpForm, horimetro_inicial: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-colors"
                      required min="0" step="0.1"
                      data-testid="non-op-horimetro-inicial"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Horímetro Final *</label>
                    <input
                      type="number"
                      value={nonOpForm.horimetro_final}
                      onChange={(e) => setNonOpForm({ ...nonOpForm, horimetro_final: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white focus:border-purple-500 focus:outline-none transition-colors"
                      required min="0" step="0.1"
                      data-testid="non-op-horimetro-final"
                    />
                  </div>
                </div>

                <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3 flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Horas não operacionais (calculado)</span>
                  <span className="text-purple-400 font-mono font-bold text-lg" data-testid="non-op-calculated">
                    {Math.max(0, (parseFloat(nonOpForm.horimetro_final) || 0) - (parseFloat(nonOpForm.horimetro_inicial) || 0)).toFixed(1)}h
                  </span>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">Observações</label>
                  <textarea
                    value={nonOpForm.observacoes}
                    onChange={(e) => setNonOpForm({ ...nonOpForm, observacoes: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-colors resize-none"
                    placeholder="Detalhes do evento (ex: translado para obra X)..."
                    rows={2}
                    data-testid="non-op-observacoes"
                  />
                </div>

                <button
                  type="submit"
                  disabled={nonOpSaving}
                  className="w-full px-4 py-3 bg-purple-500 hover:bg-purple-400 disabled:bg-gray-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
                  data-testid="non-op-submit-btn"
                >
                  <Timer size={18} />
                  {nonOpSaving ? 'Salvando...' : 'Registrar Horas Não Operacionais'}
                </button>
              </form>

              <div className="border-t border-gray-800 pt-4">
                <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <History size={16} className="text-purple-400" />
                  Lançamentos Registrados
                </h3>
                {nonOpLoading ? (
                  <div className="text-center text-gray-500 py-6">Carregando...</div>
                ) : nonOpList.length > 0 ? (
                  <div className="space-y-3" data-testid="non-op-list">
                    {nonOpList.map((rec) => (
                      <div key={rec.id} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-3">
                        <div className="flex items-start justify-between">
                          <div>
                            <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-300">{rec.categoria}</span>
                            <p className="text-gray-500 text-xs mt-1">{rec.data_hora ? new Date(rec.data_hora).toLocaleString('pt-BR') : '-'}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-purple-400 font-mono font-semibold">{rec.horas_nao_operacionais?.toFixed(1)}h</p>
                            <p className="text-gray-500 text-xs">{rec.horimetro_inicial} → {rec.horimetro_final}</p>
                          </div>
                        </div>
                        {rec.observacoes && (
                          <p className="text-gray-400 text-sm mt-2 pt-2 border-t border-gray-800">{rec.observacoes}</p>
                        )}
                        {rec.registrado_por && (
                          <p className="text-gray-500 text-xs mt-1">Registrado por: {rec.registrado_por}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Timer size={36} className="text-gray-600 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Nenhum lançamento registrado</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
